import { useCallback, useMemo, useState } from 'react';
import { type RequestToken } from '../types';
import { type HandlerAppInfo } from '../data/handlerApps';
import { useTranslation, type Locale } from '../i18n/i18n';

const LAST_USED_APP_KEY = 'lastUsedHandlerAppUuid';

const LOCALE_TO_COUNTRIES: Record<Locale, string[]> = {
  en: ['GB', 'US', 'AU', 'CA', 'IE', 'NZ'],
  nl: ['NL'],
  de: ['DE', 'AT', 'CH'],
  fr: ['FR', 'BE', 'CH'],
  sv: ['SE'],
  es: ['ES'],
};

export type TokenWithApp = {
  token: RequestToken;
  app?: HandlerAppInfo;
  name: string;
};

type AppPickerState = {
  search: string;
  setSearch: (value: string) => void;
  countryFilter: string[];
  setCountryFilter: (value: string[]) => void;
  filterOpen: boolean;
  setFilterOpen: (value: boolean) => void;
  handleSelect: (token: RequestToken) => void;
  lastUsed: TokenWithApp | null;
  recommended: TokenWithApp[];
  az: TokenWithApp[];
  hasResults: boolean;
  activeFilterCount: number;
};

export function useAppPicker(
  tokens: RequestToken[],
  handlerApps: Record<string, HandlerAppInfo>,
  recommendedHandlerAppUuids: string[],
  onSelect: (token: RequestToken) => void,
): AppPickerState {
  const { locale } = useTranslation();
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);

  const hasServerRecommendations = recommendedHandlerAppUuids.length > 0;
  const recommendedCountries = LOCALE_TO_COUNTRIES[locale] ?? [];
  // Read once per visit to the list: choosing an app always leaves it, so the
  // new choice shows as "Last used" the next time the list opens.
  const [lastUsedUuid] = useState(() =>
    localStorage.getItem(LAST_USED_APP_KEY),
  );

  const handleSelect = useCallback(
    (token: RequestToken) => {
      const uuid = token.payload.body.handlerAppUuid;
      if (uuid) localStorage.setItem(LAST_USED_APP_KEY, uuid);
      onSelect(token);
    },
    [onSelect],
  );

  const tokenItems: TokenWithApp[] = useMemo(
    () =>
      tokens.map((token) => {
        const uuid = token.payload.body.handlerAppUuid;
        const app = uuid ? handlerApps[uuid] : undefined;
        return {
          token,
          app,
          name: app?.name ?? uuid ?? 'Unknown app',
        };
      }),
    [tokens, handlerApps],
  );

  const filtered = useMemo(() => {
    let items = tokenItems;
    const q = search.trim().toLowerCase();
    if (q) {
      items = items.filter((item) => item.name.toLowerCase().includes(q));
    }
    if (countryFilter.length > 0) {
      items = items.filter((item) => {
        const appCountries = item.app?.countries ?? [];
        return appCountries.some((c) => countryFilter.includes(c));
      });
    }
    return items;
  }, [tokenItems, search, countryFilter]);

  const { lastUsed, recommended, az } = useMemo(() => {
    let lastUsedItem: TokenWithApp | null = null;
    const rec: TokenWithApp[] = [];
    const rest: TokenWithApp[] = [];

    for (const item of filtered) {
      const uuid = item.token.payload.body.handlerAppUuid;
      if (lastUsedUuid && uuid === lastUsedUuid) {
        lastUsedItem = item;
        continue;
      }
      const isRecommended = hasServerRecommendations
        ? recommendedHandlerAppUuids.includes(uuid ?? '')
        : (item.app?.countries ?? []).some((c) =>
            recommendedCountries.includes(c),
          );
      if (isRecommended) {
        rec.push(item);
      } else {
        rest.push(item);
      }
    }

    rec.sort((a, b) => a.name.localeCompare(b.name));
    rest.sort((a, b) => a.name.localeCompare(b.name));

    return { lastUsed: lastUsedItem, recommended: rec, az: rest };
  }, [
    filtered,
    hasServerRecommendations,
    recommendedHandlerAppUuids,
    recommendedCountries,
    lastUsedUuid,
  ]);

  const hasResults =
    lastUsed !== null || recommended.length > 0 || az.length > 0;
  const activeFilterCount = countryFilter.length;

  return {
    search,
    setSearch,
    countryFilter,
    setCountryFilter,
    filterOpen,
    setFilterOpen,
    handleSelect,
    lastUsed,
    recommended,
    az,
    hasResults,
    activeFilterCount,
  };
}
